import { api } from "../scripts/api.js";

/*
   <app-my-time> — a student's own active minutes, week by week.

   Exists so students can watch their own number. Active time is 80% of the
   FIN 397 grade and it is derived from an event log, so the failure mode that
   actually costs someone a grade is not a wrong formula (that can be recomputed
   from raw events at any point) but MISSING events: pane closed, not signed in,
   wrong workbook. Nothing detects that faster than the student themselves
   noticing a week that reads zero when they know they worked.

   Reads GET /engagement/me, which derives identity from the session, so there
   is no user id in the URL for anyone to edit.
*/

/*
   ⚠️ THE TARGETS USED TO LIVE HERE, AS `WEEKLY_MINIMUM = 45` AND
   `CUMULATIVE_TARGET = 1350`, AND THEY WERE SHOWN TO EVERY STUDENT ON THE
   PLATFORM.

   Those are the FIN 397 figures. ConCats was told 240 minutes a week at its
   kickoff and in the written expectations sheet, and FIN 413 has no active-time
   requirement at all, so two of the three classes were being measured on screen
   against a number nobody had agreed with them. A constant in a front-end file
   is the wrong home for a course policy: nothing about it is visible to the
   person who sets the policy, and it cannot differ per class.

   They now come from class_table via GET /engagement/me, per class, and a class
   with NO target renders NO progress bar rather than an invented one. David sets
   them on the class page.
*/

const REFRESH_MS = 60_000;

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

// "2026-09-07" -> "Sep 7". Kept dumb on purpose: the server already resolved the
// week start in the report time zone, so parsing must not re-apply a zone.
function weekLabel(iso) {
   const [y, m, d] = String(iso).split("-").map(Number);
   if (!y) return esc(iso);
   const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                   "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
   return `${months[m - 1]} ${d}`;
}

class AppMyTime extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="my-time">
            <header class="classes-header">
               <h1>My active time</h1>
            </header>
            <p class="mt-intro">
               Time you spent actively working in Excel with the task pane open.
               This counts hands on the keyboard, so it runs behind the time you
               had the file open, and reading or watching a video does not add to
               it. Weeks run Monday to Sunday. Your time is sent up about once a
               minute while you work, so a session you just finished can take a
               moment to appear. If a week still looks wrong, tell your professor
               early rather than at the end of term.
            </p>
            <p class="msg" id="mt-msg"></p>
            <div id="mt-classbar"></div>
            <div id="mt-summary"></div>
            <div id="mt-weeks"></div>
            <div id="mt-other"></div>
            <p class="mt-asof" id="mt-asof"></p>
            <div id="mt-log"></div>
         </section>
      `;
      this.load();
      this.loadLog();
      // Poll, because the page used to fetch once and never again. A student who
      // opened this tab before class and looked at it afterwards saw the zero it
      // had loaded with, and reasonably concluded their time was not being
      // recorded. It was: on 2026-08-26 the events were already in the database
      // while two students were emailing to say nothing had synced.
      this.timer = setInterval(() => this.load(), REFRESH_MS);
   }

   disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
   }

   async load() {
      const msg = this.querySelector("#mt-msg");
      if (!msg) return;              // disconnected mid-flight
      try {
         const res = await api("/engagement/me?weeks=16");
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "load_failed");
         msg.textContent = "";
         msg.className = "msg";
         this.data = data;
         // Keep the student on the class they were looking at across a refresh.
         // Re-selecting the first class every 60 seconds would yank the page out
         // from under anyone reading a second one.
         const ids = (data.classes || []).map((c) => c.class_id);
         if (this.selectedClass == null || !ids.includes(this.selectedClass)) {
            this.selectedClass = ids[0] ?? null;
         }
         this.render();
         this.stamp(data.generated_at);
         this.loaded = true;
      } catch {
         // A failed REFRESH must not blank a number that is already on screen:
         // the last good figure under an older "as of" beats an empty page. A
         // failed FIRST load has nothing to preserve, so it says so plainly.
         msg.textContent = this.loaded
            ? "Could not refresh just now. The figures below are from the time shown at the bottom."
            : "Could not load your time. Try again in a moment.";
         msg.className = "msg err";
      }
   }

   stamp(iso) {
      const el = this.querySelector("#mt-asof");
      if (!el) return;
      const when = iso ? new Date(iso) : new Date();
      const time = when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      el.textContent = `Updated as of ${time}. This page refreshes on its own about once a minute.`;
   }

   render() {
      const data = this.data || {};
      const classes = data.classes || [];
      const cls = classes.find((c) => c.class_id === this.selectedClass) || null;

      this.renderClassBar(classes);
      this.renderSummary(cls);
      this.renderWeeks(cls);
      this.renderOther(data.unattached);
   }

   /* The selector. Rendered only when there is a choice to make: a student in
      one class sees no control, because a dropdown with a single option is
      furniture that asks to be clicked and does nothing. */
   renderClassBar(classes) {
      const host = this.querySelector("#mt-classbar");
      if (classes.length <= 1) { host.innerHTML = ""; return; }
      host.innerHTML = `
         <label class="mt-classpick">Class
            <select id="mt-class">
               ${classes.map((c) => `
                  <option value="${c.class_id}"${c.class_id === this.selectedClass ? " selected" : ""}>
                     ${esc(c.label)}
                  </option>`).join("")}
            </select>
         </label>`;
      host.querySelector("#mt-class").addEventListener("change", (e) => {
         this.selectedClass = Number(e.target.value);
         this.render();
      });
   }

   renderSummary(cls) {
      const host = this.querySelector("#mt-summary");
      if (!cls) {
         host.innerHTML = `
            <div class="mt-empty">
               You are not enrolled in a class on this platform yet, so there is
               nothing to show here.
            </div>`;
         return;
      }
      const weeks = cls.weeks || [];
      if (!weeks.length) {
         host.innerHTML = `
            <div class="mt-empty">
               No active time recorded for ${esc(cls.label)} yet. It starts
               counting once you open one of this class's assignments and sign in
               to the task pane.
            </div>`;
         return;
      }

      const current = weeks[weeks.length - 1];
      const weeklyMin = cls.weekly_minimum_minutes;
      const cumTarget = cls.cumulative_target_minutes;
      const total = cls.total_minutes || 0;

      // A bar only exists where a target does. Everything else still shows its
      // number, because the minutes are true regardless of whether the class
      // measures them.
      const bar = (value, target) => target
         ? `<div class="mt-bar"><div class="mt-bar-fill" style="width:${Math.min(100, (value / target) * 100)}%"></div></div>`
         : "";
      const sub = (target, noun) => target
         ? `<div class="mt-card-sub">of a ${target} minute ${noun}</div>`
         : `<div class="mt-card-sub">${esc(cls.label)} does not set a ${noun}</div>`;

      const split = (w) => (w.logged_minutes > 0)
         ? `<div class="mt-card-note">${w.tracked_minutes.toFixed(0)} min measured
            in Excel, ${w.logged_minutes.toFixed(0)} min logged by you.</div>`
         : "";

      const openNote =
         current.open_minutes && current.open_minutes >= current.tracked_minutes + 10
            ? `<div class="mt-card-note">You had a file open for about
               ${current.open_minutes.toFixed(0)} minutes this week. Only time
               with hands on the keyboard counts toward the minimum.</div>`
            : "";

      host.innerHTML = `
         <div class="mt-cards">
            <div class="mt-card">
               <div class="mt-card-label">This week</div>
               <div class="mt-card-value">${current.active_minutes.toFixed(0)}<span>min</span></div>
               ${bar(current.active_minutes, weeklyMin)}
               ${sub(weeklyMin, "weekly minimum")}
               ${split(current)}
               ${openNote}
            </div>
            <div class="mt-card">
               <div class="mt-card-label">Term total</div>
               <div class="mt-card-value">${total.toFixed(0)}<span>min</span></div>
               ${bar(total, cumTarget)}
               ${sub(cumTarget, "term target")}
               ${(cls.total_logged_minutes || 0) > 0
                  ? `<div class="mt-card-note">${(cls.total_tracked_minutes || 0).toFixed(0)}
                     min measured, ${cls.total_logged_minutes.toFixed(0)} min logged by you.</div>`
                  : ""}
            </div>
         </div>`;
   }

   renderWeeks(cls) {
      const host = this.querySelector("#mt-weeks");
      const weeks = (cls && cls.weeks) || [];
      if (!weeks.length) { host.innerHTML = ""; return; }

      const weeklyMin = cls.weekly_minimum_minutes;
      const anyLogged = weeks.some((w) => (w.logged_minutes || 0) > 0);
      const max = Math.max(weeklyMin || 0, ...weeks.map((w) => w.active_minutes)) || 1;

      host.innerHTML = `
         <table class="cd-table mt-table">
            <thead>
               <tr><th>Week of</th><th>${anyLogged ? "Total minutes" : "Active minutes"}</th>
                   ${anyLogged ? "<th>Measured</th><th>Logged</th>" : ""}
                   <th>File open</th><th></th></tr>
            </thead>
            <tbody>
               ${weeks.slice().reverse().map((w) => {
                  // With no target there is no such thing as meeting it, so the
                  // number stays neutral rather than being colored green or amber
                  // against a threshold this class never set.
                  const cell = weeklyMin
                     ? (w.active_minutes >= weeklyMin ? "mt-met" : "mt-under")
                     : "mt-open";
                  return `
                  <tr>
                     <td>${weekLabel(w.week_start)}</td>
                     <td class="${cell}">${w.active_minutes.toFixed(0)}</td>
                     ${anyLogged
                        ? `<td class="mt-open">${(w.tracked_minutes ?? 0).toFixed(0)}</td>
                           <td class="mt-open">${(w.logged_minutes ?? 0).toFixed(0)}</td>`
                        : ""}
                     <td class="mt-open">${(w.open_minutes ?? 0).toFixed(0)}</td>
                     <td class="mt-rowbar">
                        <div class="mt-bar">
                           <div class="mt-bar-fill${weeklyMin && w.active_minutes < weeklyMin ? " under" : ""}"
                                style="width:${(w.active_minutes / max) * 100}%"></div>
                        </div>
                     </td>
                  </tr>`;
               }).join("")}
            </tbody>
         </table>`;
   }

   /* Practice in an assignment that belongs to none of this student's classes.
      Shown, and deliberately not counted toward any class: a student who worked
      should never find the time simply gone, but it must not inflate a class it
      was not for. Absent entirely when there is none, which is the normal case. */
   renderOther(other) {
      const host = this.querySelector("#mt-other");
      if (!other || !other.total_minutes) { host.innerHTML = ""; return; }
      host.innerHTML = `
         <div class="mt-other">
            <b>${other.total_minutes.toFixed(0)} min</b> of other practice, in
            assignments that are not attached to any of your classes. It is not
            counted toward a class total. If you think it should be, tell your
            professor which assignment it was.
         </div>`;
   }

   /* =====================================================================
      OFFLINE PRACTICE LOG
      ---------------------------------------------------------------------
      Tracked time only counts what happens inside an instrumented workbook
      with the pane open. An e-Invitational on another platform, a walkthrough
      video and a case worked in a plain workbook all read as zero, and for the
      ConCats squad that is most of a training week.

      ⚠️ NOT POLLED, unlike the figures above. This half of the page holds a
      form, and a refresh timer that redraws while someone is typing into it
      throws their input away. It reloads after a save or a delete, which is the
      only time it can have changed - nobody else writes a student's own log.
      ===================================================================== */

   async loadLog() {
      const host = this.querySelector("#mt-log");
      if (!host) return;
      try {
         // Which of my classes have this switched on. /me is the only source of
         // that; asking /engagement/log blind would 403 for the common case of a
         // student in no such class, and a 403 in the console reads like a bug.
         const meRes = await api("/me");
         const me = await meRes.json();
         if (!meRes.ok || !me.ok) return;

         const eligible = (me.classes || []).filter(
            (c) => c.role_in_class === "student" && c.students_log_offline_time === true
         );
         if (!eligible.length) {
            host.innerHTML = "";
            return;
         }

         const loaded = [];
         for (const c of eligible) {
            const res = await api(`/engagement/log?class=${encodeURIComponent(c.class_id)}`);
            const data = await res.json();
            if (res.ok && data.ok) loaded.push({ cls: c, log: data });
         }
         this.logState = loaded;
         this.renderLog(loaded);
      } catch {
         // A failure here must not disturb the tracked figures above, which are
         // the part of this page that matters. Staying silent is right: the
         // student loses a form they may not have been about to use.
         host.innerHTML = "";
      }
   }

   renderLog(loaded) {
      const host = this.querySelector("#mt-log");
      host.innerHTML = loaded
         .map((x, i) => this.logSectionHTML(x.cls, x.log, i))
         .join("");

      host.querySelectorAll("[data-log-save]").forEach((btn) => {
         btn.addEventListener("click", () => this.saveLog(btn.dataset.logSave));
      });
      host.querySelectorAll("[data-log-del]").forEach((btn) => {
         btn.addEventListener("click", () =>
            this.deleteLog(btn.dataset.logDel, btn.dataset.day, btn.dataset.category));
      });
      // Clicking a row loads it back into the form, which is what makes an edit
      // feel like an edit rather than a re-entry. The upsert on the server does
      // the rest: saving the same day AND the same category replaces that row,
      // while a different category on the same day is a new row beside it.
      host.querySelectorAll("[data-log-edit]").forEach((cell) => {
         cell.addEventListener("click", () => this.editLog(cell.dataset.logEdit, cell.dataset));
      });
   }

   /** Every day in the open window, newest first, as <option>s. A dropdown of
    *  the allowed days rather than a date picker, because the window is the rule
    *  most likely to be misunderstood and this way an out-of-range day is not
    *  something a student can pick and then be refused for.
    *
    *  The label carries the day's TOTAL across activity types, not one entry's
    *  minutes: since 2026-09-02 a day can hold several, and the total is the
    *  number the 4 hour cap is measured against. */
   dayOptions(win, dayTotals) {
      const totals = dayTotals || {};
      const out = [];
      const d = new Date(`${win.latest}T00:00:00Z`);
      const first = new Date(`${win.earliest}T00:00:00Z`);
      const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      while (d >= first) {
         const iso = d.toISOString().slice(0, 10);
         const total = totals[iso];
         const label = `${DAYS[d.getUTCDay()]} ${weekLabel(iso)}`;
         out.push(
            `<option value="${iso}">${esc(label)}${total ? ` (${total} min logged)` : ""}</option>`
         );
         d.setUTCDate(d.getUTCDate() - 1);
      }
      return out.join("");
   }

   logSectionHTML(cls, log, i) {
      const name = `${cls.course_code || ""} ${cls.section || ""}`.trim() || cls.title || "your class";
      const cid = cls.class_id;
      const cats = (log.categories || [])
         .map((c) => `<option value="${esc(c.value)}">${esc(c.label)}</option>`)
         .join("");

      const rows = (log.entries || []).length
         ? log.entries
              .map((e) => {
                 const label = (log.categories || []).find((c) => c.value === e.category);
                 return `
                 <tr>
                    <td class="mt-log-day" data-log-edit="${cid}"
                        data-day="${esc(e.day)}" data-minutes="${e.minutes}"
                        data-category="${esc(e.category)}" data-note="${esc(e.note || "")}"
                        title="Click to edit this day">${weekLabel(e.day)}</td>
                    <td class="mt-met">${e.minutes}</td>
                    <td>${esc(label ? label.label : e.category)}</td>
                    <td class="mt-open">${esc(e.note || "")}${
                       e.adjusted_by_staff
                          ? ` <span class="mt-log-staff" title="Entered or corrected by your instructor">adjusted</span>`
                          : ""
                    }</td>
                    <td><button class="ghost mt-log-remove" data-log-del="${cid}"
                        data-day="${esc(e.day)}" data-category="${esc(e.category)}">Remove</button></td>
                 </tr>`;
              })
              .join("")
         : `<tr><td colspan="5" class="mt-open">Nothing logged yet.</td></tr>`;

      return `
      <section class="mt-log" data-class="${cid}">
         <h2>Log practice outside Excel: ${esc(name)}</h2>
         <p class="mt-intro">
            The minutes above are measured inside the add-in, so practice that
            happens anywhere else does not appear there. Record it here and it
            counts toward your weekly total and the team board. Log what you
            actually did, in one entry per day, up to
            ${log.max_minutes_per_day} minutes. You can fill in this week and
            last week; after that ask ${esc(name)}'s instructor to add it.
            Self-reported minutes are always shown separately from measured ones.
         </p>
         <p class="msg" id="mt-log-msg-${cid}"></p>

         <div class="mt-log-form">
            <label>Day
               <select id="mt-log-day-${cid}">${this.dayOptions(log.window, log.day_totals)}</select>
            </label>
            <label>Minutes
               <input type="number" id="mt-log-min-${cid}" min="1"
                      max="${log.max_minutes_per_day}" step="1" placeholder="45">
            </label>
            <label>What you did
               <select id="mt-log-cat-${cid}">${cats}</select>
            </label>
            <label class="mt-log-note">Note (optional)
               <input type="text" id="mt-log-note-${cid}" maxlength="500"
                      placeholder="e.g. September e-Invitational">
            </label>
            <button class="btn-primary" data-log-save="${cid}">Save day</button>
         </div>

         <table class="cd-table mt-table mt-log-table">
            <thead>
               <tr><th>Day</th><th>Minutes</th><th>What</th><th>Note</th><th></th></tr>
            </thead>
            <tbody>${rows}</tbody>
         </table>
      </section>`;
   }

   editLog(cid, d) {
      this.querySelector(`#mt-log-day-${cid}`).value = d.day;
      this.querySelector(`#mt-log-min-${cid}`).value = d.minutes;
      this.querySelector(`#mt-log-cat-${cid}`).value = d.category;
      this.querySelector(`#mt-log-note-${cid}`).value = d.note || "";
      this.querySelector(`#mt-log-min-${cid}`).focus();
   }

   logMsg(cid, text, kind = "") {
      const el = this.querySelector(`#mt-log-msg-${cid}`);
      if (el) {
         el.textContent = text;
         el.className = `msg${kind ? " " + kind : ""}`;
      }
   }

   async saveLog(cid) {
      const day = this.querySelector(`#mt-log-day-${cid}`).value;
      const minutes = this.querySelector(`#mt-log-min-${cid}`).value;
      const category = this.querySelector(`#mt-log-cat-${cid}`).value;
      const note = this.querySelector(`#mt-log-note-${cid}`).value;

      this.logMsg(cid, "Saving...");
      try {
         const res = await api("/engagement/log", {
            method: "POST",
            body: JSON.stringify({ class: Number(cid), day, minutes: Number(minutes), category, note }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            // The server's reason is the authority on WHY, and it knows rules the
            // form cannot restate without duplicating them. Translated here into
            // something a student can act on, with a catch-all so a reason added
            // later still produces a sentence rather than "[object Object]".
            const why = {
               minutes_required: "Enter how many minutes you practiced.",
               minutes_not_integer: "Enter whole minutes, with no decimal point.",
               minutes_too_small: "Enter at least 1 minute.",
               minutes_too_large: `That is over the ${data.detail || 240} minute daily limit. Split a long day across the days you actually worked.`,
               day_total_too_large:
                  `That day would go over the ${data.detail || 240} minute limit. ` +
                  `You already have ${data.already_logged ?? 0} minutes logged that day, ` +
                  `so there is room for ${data.remaining ?? 0} more.`,
               invalid_category: "Pick what you were doing.",
               day_out_of_window: "That day is now closed. Ask your instructor to add it.",
               day_in_future: "You cannot log a day that has not happened yet.",
               note_too_long: "That note is too long.",
               not_authorized: "This class is not accepting logged practice.",
               migration_required: "This feature is not switched on yet. Tell your instructor.",
            }[data.reason];
            this.logMsg(cid, why || "Could not save that. Try again in a moment.", "err");
            return;
         }
         this.querySelector(`#mt-log-min-${cid}`).value = "";
         this.querySelector(`#mt-log-note-${cid}`).value = "";
         this.logMsg(cid, "Saved.");
         // Both halves: the log table, and the totals above, which this save
         // just changed. Reloading only the table would leave the student
         // looking at a weekly figure that does not include what they just
         // entered, and concluding it did not count.
         await this.loadLog();
         await this.load();
      } catch {
         this.logMsg(cid, "Could not save that. Try again in a moment.", "err");
      }
   }

   async deleteLog(cid, day, category) {
      this.logMsg(cid, "Removing...");
      try {
         // The category is REQUIRED here. Without it the server deletes the whole
         // day, which since 2026-09-02 can hold several activity types, so a
         // student removing a 20 minute video entry would silently lose the 90
         // minute competition entry sitting beside it.
         const res = await api(
            `/engagement/log?class=${encodeURIComponent(cid)}&day=${encodeURIComponent(day)}` +
               `&category=${encodeURIComponent(category)}`,
            { method: "DELETE" }
         );
         const data = await res.json();
         if (!res.ok || !data.ok) {
            this.logMsg(cid,
               data.reason === "day_out_of_window"
                  ? "That day is now closed. Ask your instructor to change it."
                  : "Could not remove that. Try again in a moment.",
               "err");
            return;
         }
         this.logMsg(cid, "Removed.");
         await this.loadLog();
         await this.load();
      } catch {
         this.logMsg(cid, "Could not remove that. Try again in a moment.", "err");
      }
   }
}

customElements.define("app-my-time", AppMyTime);
